import getConfig from "next/config";
import dynamic from "next/dynamic";

import { ArticleInitialPropsQueryVariables } from "types/api";

import PageLayout from "components/PageLayout";
import { withData, gql, UrqlPageType } from "utils/urql";
import { calculateMeterExpirationDate } from "utils/date";
import { ArticleInitialPropsQuery, ArticleLayout } from "../../../types/api";
import { NotFound } from "utils/error";
import checkArticleRenderSupport from "utils/checkArticleRenderSupport";

const { publicRuntimeConfig } = getConfig();

const SwanArticle = dynamic(() => import("components/article/Article"), {
  ssr: true,
});

const GooseArticle = dynamic(
  () => import("components/article/goose/GooseArticle"),
  {
    ssr: true,
  }
);

interface ArticleProps {
  layout: ArticleLayout;
  queryParams: string;
  swanTestGroup: boolean;
}

//We don't currently do client-side navigation, but if we did, this would not work
//because layout and queryParams would be undefined.
const Article: UrqlPageType<ArticleProps | {}, ArticleProps> = ({
  layout,
  queryParams,
  swanTestGroup,
}) => {
  const swan =
    swanTestGroup ||
    layout === "feature" ||
    (queryParams?.includes("duckling=swan") &&
      publicRuntimeConfig.ENABLE_FEATURE_ARTICLE_RENDER === "true");

  return (
    <PageLayout analyticsCategory="story page">
      {swan ? <SwanArticle /> : <GooseArticle />}
    </PageLayout>
  );
};

const ARTICLE_INITIAL_PROPS_QUERY = gql`
  query ArticleInitialPropsQuery($id: ID!, $preview: String) {
    article(id: $id, preview: $preview) {
      id
      isTnfCompatible
      layout
      hasMeter
      url
    }
  }
`;

Article.getInitialProps = async ({ urqlClient, query, req, res }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [year, month, slug, id] = query.article as Array<string>;

  const articleUrlFormat = new RegExp(
    /\/[a-z-]+\/archive\/\d{4}\/\d{2}\/[a-z\-\_\d]+\/\d+/i
  );

  //If input url doesnt match the standard format, redirect to 404 page.
  if (req?.url) {
    const requestMatchesStandardFormat = req?.url?.match(articleUrlFormat);
    if (!requestMatchesStandardFormat) {
      throw new NotFound();
    }
  }
  const preview = typeof query.preview === "string" ? query.preview : null;

  // Run an initial query to gather information required to set response headers
  // for paywall, caching, etc.
  const result = await urqlClient
    .query<ArticleInitialPropsQuery, ArticleInitialPropsQueryVariables>(
      ARTICLE_INITIAL_PROPS_QUERY,
      { id, preview }
    )
    .toPromise();

  // Checking the existence of `res` ensures code blocks only execute on
  // the server.
  if (res) {
    // Set Surrogate-Key header for Fastly cached purgin
    res.setHeader("Surrogate-Key", `article-id-${id}`);

    // If there is a preview query param, disable caching.
    if (query.preview) {
      res.setHeader("Cache-Control", "private, no-store, max-age=0");
    }
  }
  if (res && result.data?.article) {
    const swanBuckets: number[] = [99];

    // Add X-Test-Bucket-N to the list of Vary headers
    const varyHeader = res.getHeader("Vary");
    const vary = Array.isArray(varyHeader)
      ? varyHeader
      : `${varyHeader || ""}`
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);
    swanBuckets.forEach((bucket) => {
      vary.push(`X-Test-Bucket-${bucket}`);
    });
    res.setHeader("Vary", vary.join(", "));

    const { article } = result.data;
    const rawId = article.id.split(":")[1];
    const queryParams = req?.url?.split(articleUrlFormat)[1].slice(1) || "";
    const queryParamsList = queryParams.split("&");

    const swanTestGroup = swanBuckets.some(
      (bucket) =>
        req?.headers[`x-test-bucket-${bucket}`] === "1" ||
        queryParamsList.includes(`fastly_bucket=${bucket}`)
    );

    checkArticleRenderSupport({ ...result.data, swanTestGroup });

    // If there haven't been any errors, set Surrogate-Control and Cache-Control
    // headers to cache the article for three minutes in proxies and set some
    // sane defaults for stale caching.
    if (!query.preview) {
      res.setHeader("Cache-Control", "s-maxage=180");
      res.setHeader(
        "Surrogate-Control",
        "max-age=180, stale-while-revalidate=86400, stale-if-error=259200"
      );
    }

    if (article.hasMeter) {
      res.setHeader("X-Is-Metered", "1");
      res.setHeader("X-Meter-Content", rawId);

      // Generate a date representing the first day of next month.
      // Date(2020, 12, 1) is January 1st, 2021.
      const today = new Date();
      const meterExpires = calculateMeterExpirationDate(today);
      res.setHeader("X-Meter-Expires", meterExpires.toUTCString());
    }

    //If request url doesn't include api-supplied article url,
    //redirect to correct article url (with original query params).
    const articlePathname = article.url.split("theatlantic.com")[1];
    if (req?.url?.indexOf(articlePathname) === -1) {
      res.setHeader("Location", `${articlePathname}${queryParams}`);
      res.statusCode = 302;
      res.end();
    }

    return { layout: article.layout, queryParams, swanTestGroup };
  }
  return {};
};

export default withData(Article);
